import React, { useEffect, useState } from "react";
import {
    MedicineBoxOutlined,
    SaveOutlined,
    DeleteOutlined,
    ArrowLeftOutlined
} from "@ant-design/icons";

import {
    canSyncHealthData,
    getHealthHistory,
    saveHealthData,
    deleteHealthData
} from "../../api/healthTracking";

export default function LichThuoc() {
    const [medicineName, setMedicineName] = useState("");
    const [dose, setDose] = useState("");
    const [time, setTime] = useState("");
    const [note, setNote] = useState("");
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const syncWithAccount = canSyncHealthData();

    useEffect(() => {
        const loadHistory = async () => {
            setLoading(true);

            if (syncWithAccount) {
                const result = await getHealthHistory("lich_thuoc");

                if (result.success) {
                    const data = result.data.map((item) => ({
                        id: item.health_tracking_id,
                        medicineName: item.tracking_data?.medicineName,
                        dose: item.tracking_data?.dose,
                        time: item.tracking_data?.time,
                        note: item.tracking_data?.note,
                        createdDate: item.tracking_data?.createdDate,
                        database: true
                    }));

                    setHistory(data);
                } else {
                    setHistory([]);
                }
            } else {
                const savedData =
                    localStorage.getItem("lichThuocHistory");

                if (savedData) {
                    try {
                        setHistory(JSON.parse(savedData));
                    } catch (error) {
                        setHistory([]);
                    }
                } else {
                    setHistory([]);
                }
            }

            setLoading(false);
        };

        loadHistory();
    }, [syncWithAccount]);

    const reloadAccountHistory = async () => {
        const result = await getHealthHistory("lich_thuoc");

        if (!result.success) {
            return false;
        }

        const data = result.data.map((item) => ({
            id: item.health_tracking_id,
            medicineName: item.tracking_data?.medicineName,
            dose: item.tracking_data?.dose,
            time: item.tracking_data?.time,
            note: item.tracking_data?.note,
            createdDate: item.tracking_data?.createdDate,
            database: true
        }));

        setHistory(data);

        return true;
    };

    const handleSave = async () => {
        if (!medicineName.trim()) {
            alert("Vui lòng nhập tên thuốc.");
            return;
        }

        if (!time) {
            alert("Vui lòng chọn thời gian uống thuốc.");
            return;
        }

        const now = new Date();

        const newRecord = {
            id: Date.now(),
            medicineName: medicineName.trim(),
            dose: dose.trim(),
            time,
            note: note.trim(),
            createdDate: now.toLocaleDateString("vi-VN")
        };

        if (syncWithAccount) {
            setSaving(true);

            const result = await saveHealthData(
                "lich_thuoc",
                {
                    medicineName: newRecord.medicineName,
                    dose: newRecord.dose,
                    time: newRecord.time,
                    note: newRecord.note,
                    createdDate: newRecord.createdDate
                }
            );

            if (!result.success) {
                setSaving(false);

                alert(
                    "Không thể lưu lịch thuốc vào tài khoản. Vui lòng thử lại."
                );

                return;
            }

            await reloadAccountHistory();

            setSaving(false);
        } else {
            const newHistory = [
                newRecord,
                ...history
            ];

            setHistory(newHistory);

            localStorage.setItem(
                "lichThuocHistory",
                JSON.stringify(newHistory)
            );
        }

        setMedicineName("");
        setDose("");
        setTime("");
        setNote("");
    };

    const handleDelete = async (id) => {
        if (syncWithAccount) {
            const result = await deleteHealthData(id);

            if (!result.success) {
                alert(
                    "Không thể xóa lịch thuốc. Vui lòng thử lại."
                );

                return;
            }

            await reloadAccountHistory();
        } else {
            const newHistory = history.filter(
                (item) => item.id !== id
            );

            setHistory(newHistory);

            localStorage.setItem(
                "lichThuocHistory",
                JSON.stringify(newHistory)
            );
        }
    };

    return (
        <div className="max-w-screen-xl mx-auto px-3 md:px-5 pt-16 md:pt-4 pb-10">

            {/* QUAY LẠI */}
            <a
                href="/tienich/suc-khoe"
                className="inline-flex items-center text-blue-600 font-semibold text-sm mb-4"
            >
                <ArrowLeftOutlined className="mr-2" />
                Theo dõi sức khỏe
            </a>


            {/* PHẦN ĐẦU */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-500 rounded-2xl px-5 py-5 md:p-8 text-white">

                <div className="flex items-center">

                    <div className="w-12 h-12 rounded-xl bg-white bg-opacity-20 flex items-center justify-center text-2xl">
                        <MedicineBoxOutlined />
                    </div>

                    <div className="ml-4">

                        <p className="text-[11px] md:text-sm font-semibold opacity-90">
                            THEO DÕI SỨC KHỎE
                        </p>

                        <h1 className="text-[25px] md:text-3xl font-bold">
                            Lịch uống thuốc
                        </h1>

                    </div>

                </div>

                <p className="text-[14px] md:text-base mt-4 opacity-90 leading-5 md:leading-normal">
                    Tạo danh sách thuốc và thời gian sử dụng để bạn dễ theo dõi
                    lịch dùng thuốc hằng ngày.
                </p>

            </div>


            {/* TRẠNG THÁI */}
            {syncWithAccount ? (

                <div className="bg-green-50 border border-green-100 rounded-xl px-4 py-3 mt-4">

                    <p className="text-[13px] text-green-700 font-semibold">
                        ✓ Dữ liệu đang được lưu theo tài khoản
                    </p>

                    <p className="text-[12px] text-green-600 mt-1">
                        Bạn có thể đăng nhập tài khoản này trên thiết bị khác để xem lại lịch thuốc.
                    </p>

                </div>

            ) : (

                <div className="bg-yellow-50 border border-yellow-100 rounded-xl px-4 py-3 mt-4">

                    <p className="text-[13px] text-yellow-700 font-semibold">
                        Dữ liệu đang được lưu trên thiết bị này
                    </p>

                    <p className="text-[12px] text-yellow-600 mt-1">
                        Đăng nhập bằng tài khoản có mật khẩu để lưu lịch thuốc theo tài khoản.
                    </p>

                </div>

            )}


            {/* FORM */}
            <div className="bg-white border rounded-2xl p-4 md:p-6 mt-5">

                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                    Thêm lịch uống thuốc
                </h2>

                <p className="text-gray-500 text-sm mt-1">
                    Nhập thông tin thuốc bạn cần theo dõi.
                </p>


                <div className="mt-5">

                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Tên thuốc
                    </label>

                    <input
                        type="text"
                        value={medicineName}
                        onChange={(e) =>
                            setMedicineName(e.target.value)
                        }
                        placeholder="Ví dụ: tên thuốc theo toa"
                        className="w-full h-12 border rounded-xl px-3 outline-none focus:border-blue-500"
                    />

                </div>


                <div className="mt-4">

                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Liều dùng
                        <span className="font-normal text-gray-400 ml-1">
                            (không bắt buộc)
                        </span>
                    </label>

                    <input
                        type="text"
                        value={dose}
                        onChange={(e) =>
                            setDose(e.target.value)
                        }
                        placeholder="Nhập theo hướng dẫn bạn đã được kê"
                        className="w-full h-12 border rounded-xl px-3 outline-none focus:border-blue-500"
                    />

                </div>


                <div className="mt-4">

                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Thời gian uống
                    </label>

                    <input
                        type="time"
                        value={time}
                        onChange={(e) =>
                            setTime(e.target.value)
                        }
                        className="w-full h-12 border rounded-xl px-3 outline-none focus:border-blue-500"
                    />

                </div>


                <div className="mt-4">

                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Ghi chú
                        <span className="font-normal text-gray-400 ml-1">
                            (không bắt buộc)
                        </span>
                    </label>

                    <textarea
                        value={note}
                        onChange={(e) =>
                            setNote(e.target.value)
                        }
                        placeholder="Ví dụ: uống sau ăn theo hướng dẫn..."
                        rows={3}
                        className="w-full border rounded-xl px-3 py-3 outline-none focus:border-blue-500 resize-none"
                    />

                </div>


                <button
                    onClick={handleSave}
                    disabled={saving}
                    className={`w-full mt-5 h-12 rounded-xl text-white font-bold ${
                        saving
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700"
                    }`}
                >

                    <SaveOutlined className="mr-2" />

                    {saving
                        ? "Đang lưu..."
                        : "Lưu lịch thuốc"}

                </button>

            </div>


            {/* DANH SÁCH */}
            <div className="mt-7">

                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                    Danh sách thuốc
                </h2>

                <p className="text-gray-500 text-sm mt-1">
                    {syncWithAccount
                        ? "Các lịch thuốc được lưu theo tài khoản của bạn."
                        : "Các lịch thuốc bạn đã lưu trên thiết bị này."}
                </p>


                {loading ? (

                    <div className="bg-white border rounded-2xl text-center p-8 mt-4">

                        <p className="text-sm text-gray-500">
                            Đang tải dữ liệu...
                        </p>

                    </div>

                ) : history.length === 0 ? (

                    <div className="bg-white border rounded-2xl text-center p-8 mt-4">

                        <MedicineBoxOutlined className="text-3xl text-gray-300" />

                        <p className="font-semibold text-gray-700 mt-3">
                            Chưa có lịch thuốc
                        </p>

                        <p className="text-sm text-gray-500 mt-1">
                            Thuốc bạn thêm sẽ xuất hiện tại đây.
                        </p>

                    </div>

                ) : (

                    <div className="space-y-3 mt-4">

                        {history.map((item) => (

                            <div
                                key={item.id}
                                className="bg-white border rounded-2xl p-4"
                            >

                                <div className="flex items-start justify-between">

                                    <div className="flex-1 pr-3">

                                        <h3 className="font-bold text-lg text-gray-900">
                                            {item.medicineName}
                                        </h3>


                                        <div className="mt-2 flex flex-wrap gap-2">

                                            {item.dose && (
                                                <span className="bg-green-50 text-green-700 text-xs font-semibold px-2 py-1 rounded-lg">
                                                    {item.dose}
                                                </span>
                                            )}

                                            <span className="bg-blue-50 text-blue-700 text-xs font-semibold px-2 py-1 rounded-lg">
                                                {item.time}
                                            </span>

                                        </div>


                                        {item.note && (
                                            <p className="text-sm text-gray-500 mt-3">
                                                {item.note}
                                            </p>
                                        )}


                                        {item.createdDate && (
                                            <p className="text-xs text-gray-400 mt-2">
                                                Đã tạo: {item.createdDate}
                                            </p>
                                        )}

                                    </div>


                                    <button
                                        onClick={() =>
                                            handleDelete(item.id)
                                        }
                                        className="w-10 h-10 rounded-xl bg-red-50 text-red-500 flex items-center justify-center flex-shrink-0"
                                        aria-label="Xóa lịch thuốc"
                                    >
                                        <DeleteOutlined />
                                    </button>

                                </div>

                            </div>

                        ))}

                    </div>

                )}

            </div>


            {/* LƯU Ý */}
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 mt-7">

                <p className="font-semibold text-gray-800 text-sm">
                    Lưu ý
                </p>

                {syncWithAccount ? (

                    <p className="text-[13px] text-gray-600 leading-5 mt-1">
                        Lịch thuốc được lưu theo tài khoản đang đăng nhập và có thể được tải lại trên thiết bị khác khi sử dụng cùng tài khoản.
                    </p>

                ) : (

                    <p className="text-[13px] text-gray-600 leading-5 mt-1">
                        Lịch thuốc hiện được lưu trên trình duyệt của thiết bị bạn đang sử dụng.
                        Nếu xóa dữ liệu trình duyệt hoặc đổi thiết bị, lịch sử có thể không còn.
                    </p>

                )}

                <p className="text-[13px] text-gray-500 leading-5 mt-2">
                    Chỉ nhập thông tin thuốc và cách dùng theo hướng dẫn bạn đã nhận được từ bác sĩ hoặc dược sĩ.
                    Công cụ này không tự đề xuất thuốc hoặc thay đổi liều điều trị.
                </p>

            </div>

        </div>
    );
}